import { useContext, useEffect, useState } from 'react'
import BasicModal from '../../../../../ui/BasicModal'
import { Button, Title } from '../../../../../ui'
import GlobalContext from '../../../../../../../context/global-context'

const OBLIGATION_OPTIONS = [
  { value: 'pago-anterior', label: 'Pago Documento año anterior' },
  { value: 'pago-anterior-previred', label: 'Pago Previred año anterior' },
  { value: 'pago-anterior-remuneraciones', label: 'Pago Remuneraciones año anterior' }
]

const PREVIRED_DATA = {
  razonSocial: 'Previred',
  rut: '96.929.390-0'
}

const DOCUMENT_TYPE_LOCKED_OBLIGATIONS = [
  'pago-anterior-previred',
  'pago-anterior-remuneraciones'
]

const REFERENCE_YEAR = new Date().getFullYear() - 1
const REFERENCE_DATE_MIN = `${REFERENCE_YEAR}-01-01`
const REFERENCE_DATE_MAX = `${REFERENCE_YEAR}-12-31`

const getDefaultReferenceDate = () => {
  const date = new Date()
  date.setFullYear(REFERENCE_YEAR)

  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${REFERENCE_YEAR}-${month}-${day}`
}

const normalizeReferenceDate = value => {
  const normalizedValue = String(value || '').slice(0, 10)

  return normalizedValue.startsWith(`${REFERENCE_YEAR}-`)
    ? normalizedValue
    : getDefaultReferenceDate()
}

const buildDraftData = modalData => ({
  ...EMPTY_DOCUMENT,
  ...modalData,
  fecha: normalizeReferenceDate(modalData?.fecha)
})

const EMPTY_DOCUMENT = {
  razonSocial: '',
  rut: '',
  fecha: '',
  descripcion: '',
  folio: '',
  monto: 0,
  tipoObligacion: 'pago-anterior'
}

const InputField = ({ label, placeholder, type, value, onChange, disabled = false }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`mt-1 block w-full rounded-full border-gray-300 shadow-sm sm:text-sm
        ${
          disabled
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : 'focus:border-indigo-500 focus:ring-indigo-500'
        }
      `}
    />
  </div>
)

const ModalDocumentos = ({ open, onClose, modalData, setModalData, showObligationTypes = false }) => {
  const { currentBusiness } = useContext(GlobalContext)
  const [draftData, setDraftData] = useState(() => buildDraftData(modalData))
  const isDocumentTypeLocked =
    showObligationTypes &&
    DOCUMENT_TYPE_LOCKED_OBLIGATIONS.includes(draftData.tipoObligacion)

  useEffect(() => {
    if (open) {
      setDraftData(buildDraftData(modalData))
    }
  }, [open])

  const handleSave = () => {
    setModalData(draftData)
    onClose()
  }

  const handleObligationChange = tipoObligacion => {
    setDraftData(previous => {
      if (tipoObligacion === 'pago-anterior-previred') {
        return { ...previous, ...PREVIRED_DATA, folio: '', tipoObligacion }
      }

      if (tipoObligacion === 'pago-anterior-remuneraciones') {
        return {
          ...previous,
          razonSocial: currentBusiness?.name || '',
          rut: currentBusiness?.rut ? formatRut(currentBusiness.rut) : '',
          folio: '',
          tipoObligacion
        }
      }

      return {
        ...previous,
        razonSocial: modalData?.razonSocial || '',
        rut: modalData?.rut || '',
        folio: modalData?.folio || '',
        tipoObligacion
      }
    })
  }

  const formatRut = input => {
    let cleaned = input.replace(/[^0-9kK]/g, '').toUpperCase()
    if (cleaned.length > 9) cleaned = cleaned.slice(0, 9)
    const body = cleaned.slice(0, -1)
    const dv = cleaned.slice(-1)
    let formattedBody = ''
    for (let i = body.length - 1, j = 1; i >= 0; i--, j++) {
      formattedBody = body[i] + formattedBody
      if (j % 3 === 0 && i !== 0) {
        formattedBody = '.' + formattedBody
      }
    }
    if (body.length === 0) return dv
    return `${formattedBody}-${dv}`
  }

  const formatMonto = value => {
    if (!value) return ''
    const numericValue = value.replace(/\D/g, '')
    return new Intl.NumberFormat('es-CL').format(numericValue)
  }
  return (
    <div>
      <BasicModal
        open={open}
        onClose={onClose}
        footer={
          <>
            <Button
              onClick={onClose}
              className='text-[10px] bg-primary text-white rounded-full'
              title='Cerrar'
            />
            <Button
              onClick={handleSave}
              className='text-[10px] bg-accent2 text-white rounded-full'
              title='Guardar'
            />
          </>
        }
        width={600}
        height={550}
      >
        <div className='mt-4'>
          <div className='flex justify-between items-center mb-6'>
            <div>
              <Title className='text-3xl font-sans'>Datos referencia</Title>
            </div>
          </div>
        <div className="flex w-full items-start h-full p-6 bg-white rounded-lg shadow-md'">
              <div className='flex flex-col gap-6 w-full'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full'>
          {showObligationTypes && <div className='md:col-span-2'>
            <label className='block text-sm font-sans font-medium text-gray-700'>
              Tipo obligación
            </label>
            <select
              value={draftData.tipoObligacion || 'pago-anterior'}
              onChange={e => handleObligationChange(e.target.value)}
              className='mt-1 block w-full rounded-full border-gray-300 bg-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
            >
              {OBLIGATION_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>}
          <InputField
            label='Razón Social'
            placeholder='razón social'
            value={draftData.razonSocial || ''}
            onChange={e =>
              setDraftData(prev => ({
                ...prev,
                razonSocial: e.target.value
              }))
            }
            disabled={showObligationTypes && draftData.tipoObligacion !== 'pago-anterior'}
          />

          <InputField
            label='RUT'
            placeholder='11.111.111-1'
            value={draftData.rut || ''}
            onChange={e =>
              setDraftData(prev => ({
                ...prev,
                rut: formatRut(e.target.value)
              }))
            }
            disabled={showObligationTypes && draftData.tipoObligacion !== 'pago-anterior'}
          />

          <div>
            <label className='block text-sm font-sans font-medium text-gray-700'>Fecha</label>
            <input
              type='date'
              value={draftData.fecha || ''}
              min={REFERENCE_DATE_MIN}
              max={REFERENCE_DATE_MAX}
              className='mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
              onChange={e =>
                setDraftData(prev => ({
                  ...prev,
                  fecha: e.target.value
                }))
              }
            />
          </div>
          <InputField
            label='Tipo Documento'
            placeholder='tipo documento'
            value={draftData.descripcion || ''}
            onChange={e =>
              setDraftData(prev => ({
                ...prev,
                descripcion: e.target.value
              }))
            }
            disabled={isDocumentTypeLocked}
          />

          <InputField
            label='Folio'
            placeholder='Ingrese folio'
            value={draftData.folio || ''}
            onChange={e =>
              setDraftData(prev => ({
                ...prev,
                folio: e.target.value
              }))
            }
            disabled={showObligationTypes && draftData.tipoObligacion !== 'pago-anterior'}
          />

          <InputField
            label='Monto'
            placeholder='$'
            value={formatMonto(draftData.monto?.toString() || '')}
            onChange={e => {
              const raw = e.target.value.replace(/\D/g, '')
              setDraftData(prev => ({
                ...prev,
                monto: Number(raw)
              }))
            }}
          />
          </div>
          </div>
          </div>
        </div>
      </BasicModal>
    </div>
  )
}

export default ModalDocumentos
